<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Device Config {{ strOperations().capitalize(type) }} Files</h1>
                <v-btn color="black" prepend-icon="mdi-plus"
                       @click="toggleDialog()">Add
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <template v-if="type=='master'">
                            <th-btn :sortedColumn="sortedColumn" column="name" sortable title="Name" @sort="sort"/>
                        </template>
                        <template v-else>
                            <th-btn :sortedColumn="sortedColumn" column="slno" sortable title="Device" @sort="sort"/>
                            <th-btn :sortedColumn="sortedColumn" column="master_config" sortable title="Master Config"
                                    @sort="sort"/>
                            <th-btn :sortedColumn="sortedColumn" column="download_needed" sortable
                                    title="Download Needed"
                                    @sort="sort"/>
                            <th-btn :sortedColumn="sortedColumn" column="downloading_queued" sortable
                                    title="Downloading Queued"
                                    @sort="sort"/>
                        </template>
                        <th-btn :sortedColumn="sortedColumn" column="created_at" sortable title="Created at"
                                @sort="sort"/>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="config in configs" v-if="configs.length" class="text-left">
                        <template v-if="type==='master'">
                            <td>{{ config.name }}</td>
                        </template>
                        <template v-else>
                            <td>{{ config.slno }}</td>
                            <td>{{ config.master_config }}</td>
                            <td class="text-center">
                                <v-icon :icon="config.download_needed?'mdi-check-circle':'mdi-close-circle'"/>
                            </td>
                            <td class="text-center">
                                <v-icon :icon="config.downloading_queued?'mdi-check-circle':'mdi-close-circle'"/>
                            </td>
                        </template>

                        <td>{{ formatDateTime(config.created_at) }}</td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn :disabled="dialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small"
                                       @click="toggleDialog('put',config.id)">
                                    Edit
                                </v-btn>
                                <v-btn class="text-info" prepend-icon="mdi-content-copy" size="x-small"
                                       @click="toggleDialog('post',config.id)">
                                    Copy
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteConfig(config)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" :colspan="type==='master'?4:7" class="text-center">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else :colspan="type==='master'?4:7" class="text-center">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchConfigs"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <component :is="CreateEditConfig" v-if="dialogSwitch"
               v-model="dialogSwitch"
               :config="selectedConfig"
               :method="method"
               @addConfig="addConfig"
               @updateConfig="updateConfig"
    />
</template>

<script setup>
import router from "@/router";
import {computed, onMounted, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import ThBtn from "@/components/globals/th-btn.vue";
import {strOperations} from "@/composables/globals";
import {formatDateTime} from '@/composables/useTimezone';
import {useSnackbarStore} from "@/stores/snackBarStore";

const CreateEditConfig = ref(null);
const type = computed(() => router.currentRoute.value.params.type || null);
watch(type, (newType) => {
    loadComponent()
    fetchConfigs()
});

async function loadComponent() {
    try {
        // Dynamically import the component
        const componentModule = await import(`@/components/dialogs/devices/configs/${type.value === 'master' ? 'Master_' : ''}Create_Edit.vue`);
        CreateEditConfig.value = componentModule.default;
    } catch (error) {
        console.error("Component import failed:", error);
        // Handle the error (e.g., show a fallback component or a message)
    }
}

const {showSnackbar} = useSnackbarStore();
const dialogSwitch = ref(false);
const selectedConfig = ref(null);
const sortedColumn = ref(null);
const sortedType = ref(null);
const totalConfigs = ref(0);
const method = ref("post");
const currentPage = ref(1);
const totalPages = ref(1);
const loading = ref(true);
const configs = ref([]);

const toggleDialog = (action = 'post', config = null) => {
    selectedConfig.value = config;
    dialogSwitch.value = true;
    method.value = action;
}
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchConfigs();
}
const addConfig = (config) => {
    configs.value.unshift(config);
}
const updateConfig = (config) => {
    configs.value.map((item, index) => {
        if (item.id === config.id) {
            configs.value[index] = config;
        }
    });
}
onMounted(() => {
    loadComponent();
    fetchConfigs();
});

const fetchConfigs = () => {
    send('get', `admin/device-configs`, {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        [type.value]: 1
    })
        .then((response) => {
            configs.value = response.data.data.data;
            totalConfigs.value = response.data.data.total;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('configs could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
const deleteConfig = (config) => {
    if (confirm(`Are you sure to delete "${config.name}" config file?`)) {
        send('delete', `admin/device-configs/${config.id}${type.value === 'master' ? '?master=1' : ''}`)
            .then((response) => {
                if (response.data.success) {
                    configs.value.map((item, index) => {
                        if (item.id === config.id) {
                            configs.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${config.name}" config file could not be deleted`, 'error')
                }
            })
    }
}
</script>
